import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useQueryClient } from '@tanstack/react-query'
import MessageBox from './MessageBox'
import { withBase } from '../config/api'
import Overlay from './Overlay'
import {
  modalPrimaryButtonStyle,
    alternativeButtonStyle
} from '../styles/buttonStyles'
import showPwd from '../assets/images/icons/show_pwd.png'
import hidePwd from '../assets/images/icons/hide_pwd.png'

const LoginModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  const queryClient = useQueryClient()
  const [showPassword, setShowPassword] = useState(false)

  const [hoverBtn, setHoverBtn] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showForgot, setShowForgot] = useState(false)
  const [loginMessage, setLoginMessage] = useState(null)
  const [showMFALogin, setShowMFALogin] = useState(false)

  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
    mfaCode: ''
  })

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose])

useEffect(() => {
  if (!open) {
    setLoginMessage(null)
    setLoginForm({ email: '', password: '', mfaCode: '' })
    setShowForgot(false)
    setShowMFALogin(false)
    setHoverBtn(null)
    setPressedBtn(null)
  }
}, [open])

  const updateLoginForm = (k, v) =>
    setLoginForm(prev => ({ ...prev, [k]: v }))

  const fromServerMessage = data =>
    data?.message || 'Error'

const invalidMFAMessage = () => {
  const message = t('MFA_CODE_INVALID')
  return message === 'MFA_CODE_INVALID'
    ? 'Nesprávny MFA kód'
    : message
}

  const login = async e => {
    if (e) e.preventDefault()
    if (isLoading) return

    if (!loginForm.email) {
      setLoginMessage({ message: t('EMAIL_REQUIRED'), type: 'error' })
      return
    }

    if (!loginForm.password) {
      setLoginMessage({ message: t('PASSWORD_REQUIRED'), type: 'error' })
      return
    }

    setIsLoading(true)
    setLoginMessage(null)

    try {
      const res = await fetch(withBase('/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language
        },
        credentials: 'include',
        body: JSON.stringify(loginForm)
      })

      const data = await res.json()

if (data?.mfaRequired) {
  setShowMFALogin(true)

  if (!loginForm.mfaCode) {
    setLoginMessage(null)
  } else {
    setLoginMessage({
      message: invalidMFAMessage(),
      type: 'error'
    })
  }

  setIsLoading(false)
  return
}

      if (!res.ok) {
        setLoginMessage({
          message: fromServerMessage(data),
          type: 'error'
        })
        setIsLoading(false)
        return
      }

	await refreshUser()
	await queryClient.clear()
	onClose()
	navigate('/home', { replace: true })
    } catch {
      setLoginMessage({
        message: t('SERVER-ERROR') || 'Server nie je dostupný',
        type: 'error'
      })
      setIsLoading(false)
    }
  }

  const resendConfirmation = async () => {
    if (isLoading) return
    if (!loginForm.email) {
      setLoginMessage({ message: t('EMAIL_REQUIRED'), type: 'error' })
      return
    }

    setIsLoading(true)
    setLoginMessage(null)

    try {
      const res = await fetch(withBase('/auth/resend-confirmation'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language
        },
        body: JSON.stringify({ email: loginForm.email })
      })

      const data = await res.json()
      setLoginMessage({
        message: fromServerMessage(data),
        type: res.ok ? 'success' : 'error'
      })
    } catch {
      setLoginMessage({
        message: t('SERVER-ERROR') || 'Server nie je dostupný',
        type: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const forgotPassword = async () => {
    if (isLoading) return
    if (!loginForm.email) {
      setLoginMessage({ message: t('EMAIL_REQUIRED'), type: 'error' })
      return
    }

    setIsLoading(true)
    setLoginMessage(null)

    try {
      const res = await fetch(withBase('/auth/forgot-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language
        },
        body: JSON.stringify({ email: loginForm.email })
      })

      const data = await res.json()
      setLoginMessage({
        message: fromServerMessage(data),
        type: res.ok ? 'success' : 'error'
      })
    } catch {
      setLoginMessage({
        message: t('SERVER-ERROR') || 'Server nie je dostupný',
        type: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

return (
  <>
    <Overlay open={isLoading} zIndex={4000} backdropOpacity={0.75} />

    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        style={{
          background: '#122629',
          padding: 20,
          border: '1px solid #107c6b',
          borderRadius: 12,
          boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
          width: 'clamp(320px, 30vw, 420px)',
display: 'flex',
flexDirection: 'column',
alignItems: 'center'

        }}
      >
        <h3 style={{ fontSize: 28, margin: 0, marginBottom: 24, color: '#d9ffe4' }}>
          {t('LOGIN')}
        </h3>

        {loginMessage && (
          <MessageBox
            message={loginMessage.message}
            type={loginMessage.type}
          />
        )}

        <form onSubmit={login}>
          <input
 	    autoFocus
            disabled={isLoading}
            placeholder={t('EMAIL')}
            value={loginForm.email}
            onChange={e => updateLoginForm('email', e.target.value)}
            style={{
              width: '100%',
              height: 44,
              fontSize: 15,
              padding: '0 18px',
              marginBottom: 12,
              border: '1.5px solid #1f8d7b', background: '#ffffff', color: '#000000',
              boxSizing: 'border-box'
            }}
          />

<input
  disabled={isLoading}
  type={showPassword ? 'text' : 'password'}
  placeholder={t('PASSWORD')}
  value={loginForm.password}
  onChange={e => updateLoginForm('password', e.target.value)}
  onClick={(e) => {
    const rect = e.target.getBoundingClientRect()
    if (e.clientX > rect.right - 40) {
      setShowPassword(prev => !prev)
    }
  }}
  onMouseMove={(e) => {
    const rect = e.target.getBoundingClientRect()
    e.target.style.cursor = e.clientX > rect.right - 40 ? 'pointer' : 'text'
  }}
  style={{
    width: '100%',
    height: 44,
    fontSize: 15,
    padding: '0 18px',
    marginBottom: 16,
    border: '1.5px solid #1f8d7b', background: '#ffffff', color: '#000000',
    boxSizing: 'border-box',
    backgroundImage: `url(${showPassword ? hidePwd : showPwd})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '20px'
  }}
/>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...modalPrimaryButtonStyle(
                pressedBtn,
                'login-submit',
                hoverBtn === 'login-submit'
              ),
              width: '100%',
              marginBottom: 8
            }}
            onMouseEnter={() => setHoverBtn('login-submit')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('login-submit')}
            onMouseUp={() => setPressedBtn(null)}
          >
            {t('LOGIN')}
          </button>
        </form>

        <button
          style={{
            ...alternativeButtonStyle(
              pressedBtn,
              'resend',
              hoverBtn === 'resend'
            ),
            width: '100%',
            marginBottom: 8
          }}
          onMouseEnter={() => setHoverBtn('resend')}
          onMouseLeave={() => {
            setHoverBtn(null)
            setPressedBtn(null)
          }}
          onMouseDown={() => setPressedBtn('resend')}
          onMouseUp={() => setPressedBtn(null)}
          onClick={resendConfirmation}
          disabled={isLoading}
        >
          {t('RESEND_CONFIRM')}
        </button>

        {!showForgot && (
          <button
            style={{
              ...alternativeButtonStyle(
                pressedBtn,
                'forgot',
                hoverBtn === 'forgot'
              ),
              width: '100%',
              marginBottom: 8
            }}
            onMouseEnter={() => setHoverBtn('forgot')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('forgot')}
            onMouseUp={() => setPressedBtn(null)}
            onClick={() => setShowForgot(true)}
            disabled={isLoading}
          >
            {t('FORGOT_PASSWORD')}
          </button>
        )}

        {showForgot && (
          <button
            style={{
              ...modalPrimaryButtonStyle(
                pressedBtn,
                'reset',
                hoverBtn === 'reset'
              ),
              width: '100%',
              marginBottom: 8
            }}
            onMouseEnter={() => setHoverBtn('reset')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('reset')}
            onMouseUp={() => setPressedBtn(null)}
            onClick={forgotPassword}
            disabled={isLoading}
          >
            {t('SEND_RESET_LINK')}
          </button>
        )}

        <button
          style={alternativeButtonStyle(
            pressedBtn,
            'close',
            hoverBtn === 'close'
          )}
          onMouseEnter={() => setHoverBtn('close')}
          onMouseLeave={() => {
            setHoverBtn(null)
            setPressedBtn(null)
          }}
          onMouseDown={() => setPressedBtn('close')}
          onMouseUp={() => setPressedBtn(null)}
          onClick={onClose}
          disabled={isLoading}
        >
          {t('CLOSE')}
        </button>
      </div>
    </div>
    {showMFALogin && (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1200
        }}
      >
        <div
          style={{
            background: '#122629',
            padding: 20,
            border: '2px solid #107c6b',
            borderRadius: 12,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: 'clamp(300px, 28vw, 380px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <h3 style={{ fontSize: 24, margin: 0, marginBottom: 16, color: '#d9ffe4' }}>
            {t('MFA_AUTH_APP')}
          </h3>

          {loginMessage && (
            <MessageBox
              message={loginMessage.message}
              type={loginMessage.type}
            />
          )}

          <form onSubmit={login} style={{ width: '100%' }}>
            <input
              autoFocus
              disabled={isLoading}
              placeholder={t('MFA_CODE')}
              value={loginForm.mfaCode}
              onChange={e => updateLoginForm('mfaCode', e.target.value)}
              style={{
                width: '100%',
                height: 44,
                fontSize: 15,
                padding: '0 18px',
                marginBottom: 16,
                border: '1.5px solid #1f8d7b',
                background: '#ffffff',
                color: '#000000',
                boxSizing: 'border-box'
              }}
            />

            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...modalPrimaryButtonStyle(
                  pressedBtn,
                  'mfa-login-submit',
                  hoverBtn === 'mfa-login-submit'
                ),
                width: '100%',
                marginBottom: 8
              }}
              onMouseEnter={() => setHoverBtn('mfa-login-submit')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('mfa-login-submit')}
              onMouseUp={() => setPressedBtn(null)}
            >
              {t('LOGIN')}
            </button>
          </form>

          <button
            style={{
              ...alternativeButtonStyle(
                pressedBtn,
                'mfa-login-cancel',
                hoverBtn === 'mfa-login-cancel'
              ),
              width: '100%'
            }}
            onMouseEnter={() => setHoverBtn('mfa-login-cancel')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('mfa-login-cancel')}
            onMouseUp={() => setPressedBtn(null)}
            onClick={() => {
              setShowMFALogin(false)
              updateLoginForm('mfaCode', '')
              setLoginMessage(null)
            }}
            disabled={isLoading}
          >
            {t('CANCEL')}
          </button>
        </div>
      </div>
    )}
  </>
  )
}

export default LoginModal
